import { CommonModule } from "@angular/common";
import { Component, inject, model } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "../../shared.module";
import { FormsModule, ReactiveFormsModule, ValidatorFn, Validators, FormControl } from "@angular/forms";
import { FormInputComponent } from "../ui-components/forms/form-input/form-input.component";
import { FormContainerComponent } from "../ui-components/forms/form-container/form-container.component";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentDialogBase } from "../payment-dialog-base/payment-dialog-base";
import { takeUntil, from, of, lastValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SharedApiService } from "src/app/services/api/sharedApi.service";
import { NgxSpinnerService } from "ngx-spinner";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { IExecuteProcedureRequest, IExecuteProcedureResult, IGetCashMovementsSummaryRequest, IGetCashMovementsSummaryResponse } from "src/app/services/model/pos.api.model";

@Component({
  selector: "app-cash-payment-dialog",
  templateUrl: "./cash-payment-dialog.component.html",
  styleUrls: ["./cash-payment-dialog.component.scss"],
  imports: [
    TranslateModule,
    CommonModule,
    SharedModule,
    TranslateModule,
    FormsModule,
    FormInputComponent,
    FormContainerComponent,
    ReactiveFormsModule,
  ],
  standalone: true,
})
export class CashPaymentDialogComponent extends PaymentDialogBase {
  
  readonly checked = model(false);
  public valueToReturnFormatted: string | null = null;
  public availableCash: number | null = null;
  private maxCashPayment: { value?: number | null } | null = null;

  private readonly _sharedApiService = inject(SharedApiService);
  private readonly _spinner = inject(NgxSpinnerService);
  
  get tenderType(): TenderType {
    return TenderType.Cash;
  }

  override async ngOnInit() {
    this.maxCashPayment = await this.paymentTypesService.getMaxCashPayment();
    super.ngOnInit();
    this.confirmForm?.get("amountToPay")?.updateValueAndValidity({ emitEvent: false });
    if (this.isReturn) {
      await this.loadAvailableCash();
    }
  }

  private async loadAvailableCash(): Promise<void> {
    this._spinner.show();

    const cashPaymentName = await this._sharedService.getCashPaymentTypeForCurrency();

    if (!cashPaymentName) {
      console.error('Cash payment type not found for user currency.');
      this._spinner.hide();
      return;
    }

    const request: IExecuteProcedureRequest<IGetCashMovementsSummaryRequest> = {
      DbScheme: DbSchemeEnum.Dert,
      ProcedureName: ProcedureNameEnum.GetCashMovementsSummary,
      Parameters: {
        CurrencyCode: this.loginService.user?.CurrencyCode,
        PaymentName: cashPaymentName.Name, // Not needed for cash, can be used for other payment types if needed
        ReturnToResult: 0,
      } as IGetCashMovementsSummaryRequest,
    };

    const result = await lastValueFrom(
      from(this._sharedApiService.executeProcedure<IGetCashMovementsSummaryRequest, IExecuteProcedureResult<IGetCashMovementsSummaryResponse>>(request)).pipe(
        catchError(error => { console.error('Failed to load cash movements summary', error); return of(null); })
      )
    );

    if (result?.data?.TotalMovement !== undefined && result.data.TotalMovement !== null) {
      this.availableCash = result.data.TotalMovement;
    }

    // The available-cash limit is loaded after the form is created. Re-run the
    // amount validator so a value entered/prefilled before the response cannot
    // remain valid after the cash balance is known.
    this.confirmForm?.get("amountToPay")?.updateValueAndValidity({ emitEvent: false });

    this._spinner.hide();
  }

  get maxAllowedAmountFormatted(): string | null {
    const maxAllowed = this.getMaxAllowedAmount();
    if (maxAllowed !== null && maxAllowed !== undefined && maxAllowed !== Infinity) {
      return this.currencyPipe.transform(maxAllowed, this.stateService.currencyCode()) || null;
    }
    return null;
  }

  get maximumAmountFormatted(): string | null {
    const maximumAmount = this.isReturn
      ? this.getMaxAllowedAmount()
      : this.effectiveConfiguredMaxPaymentValue;
    if (maximumAmount !== null && maximumAmount !== undefined && maximumAmount !== Infinity) {
      return this.currencyPipe.transform(maximumAmount, this.stateService.currencyCode(), true, true) || null;
    }
    return null;
  }
  
  protected override setupFormSubscriptions() {
    super.setupFormSubscriptions();

    this.confirmForm
      .get("amountReceived")
      ?.valueChanges.pipe(takeUntil(this.destroyObservable$))
      .subscribe((value) => this.updateValueToReturn(value));

    this.confirmForm
      .get("amountToPay")
      ?.valueChanges.pipe(takeUntil(this.destroyObservable$))
      .subscribe(() => {
        const amountReceived = this.confirmForm.get("amountReceived")?.value;
        if (amountReceived !== null && amountReceived !== undefined) {
          this.updateValueToReturn(amountReceived);
        }
      });
  }

  private updateValueToReturn(amountReceived: number | null) {
    if (amountReceived === null || amountReceived === undefined) {
      this.valueToReturnFormatted = null;
      return;
    }

    const amountToPayValue = this.confirmForm.get("amountToPay")?.value ?? this.amountToPay;
    const change = Number(amountReceived) - Number(amountToPayValue);
    this.valueToReturnFormatted =
      this.currencyPipe.transform(change, this.stateService.currencyCode()) || null;
  }

  protected getPaymentTranslationKey(): string {
    return "webPos.CashPayment";
  }
  
  protected getAmountValidators(): ValidatorFn[] {
    return [Validators.min(1), this.validateMaxAllowedAmount()];
  }

  private validateMaxAllowedAmount(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const value = Number(control.value);

      if (isNaN(value)) {
        return { invalidValue: true };
      }

      const maxAllowed = this.getMaxAllowedAmount();

      if (maxAllowed === null || maxAllowed === undefined || maxAllowed === Infinity) {
        return null;
      }

      if (value === null || value === undefined) {
        return null;
      }

      if (value > maxAllowed) {
        return { maxAllowedExceeded: true };
      }

      return null;
    };
  }

  private getMaxAllowedAmount(): number {
    const flowLimit = this.isOverpaymentOverrideEnabled()
      ? Infinity
      : (this.isReturn ? this.amountToReturn : (this.data?.remainingToPay ?? Infinity));
    const cashLimit = this.isReturn && this.availableCash !== null ? this.availableCash : Infinity;
    const maxPaymentLimit = this.isReturn
      ? this.getConfiguredReturnCashLimit()
      : this.getMaximumPaymentConfigLimit();
    return Math.min(
      this.effectiveConfiguredMaxPaymentValue ?? Infinity,
      maxPaymentLimit,
      flowLimit,
      cashLimit
    );
  }

  private getConfiguredReturnCashLimit(): number {
    const configuredLimits = [
      this.stateService.configuration?.maxCashPaymentValue,
    ]
      .filter(value => value !== null && value !== undefined)
      .map(value => Number(value))
      .filter(Number.isFinite);

    if (this.maxCashPayment?.value !== null && this.maxCashPayment?.value !== undefined) {
      const maximumPaymentValue = Number(this.maxCashPayment.value);
      if (Number.isFinite(maximumPaymentValue)) {
        configuredLimits.push(maximumPaymentValue);
      }
    }

    return configuredLimits.length > 0 ? Math.min(...configuredLimits) : Infinity;
  }

  private getMaximumPaymentConfigLimit(): number {
    const configuredValue = this.maxCashPayment?.value;
    if (configuredValue === null || configuredValue === undefined) {
      return Infinity;
    }

    const maximumPaymentValue = Number(configuredValue);
    return Number.isFinite(maximumPaymentValue) ? maximumPaymentValue : Infinity;
  }
  
  public deselectAllPayments() {
    const checkbox = document.getElementById("SelectAllCheckbox") as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = false;
    }
    this._selectedPayments = [];
    this.selectedDueDates = [];
    this.confirmForm.get("amountToPay")?.setValue(null);
  }
}
